import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useMemo, useState, useEffect } from 'react';
import useQueryUrl from 'src/hooks/useQuery';
import { useLocation } from 'react-router';
import TableSelectGroupPortal from 'src/components/TableSelectGroupPortal';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { IGroupPortal } from 'src/models/groupPortal';
import useGroupPortal from 'src/services/useGroupPortal';
import {
  toggleSelectedGroupPortalIdOnm,
  toggleSelectedGroupPortalIdOnmExclude
} from 'src/features/groupPortal';
import {
  reducerUpdateAdvanceSearchOnmExcludeMediaList,
  reducerUpdateAdvanceSearchOnmMediaList
} from 'src/features/advanceSearchOnlineMedia';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import SelectMultipleLanguage from 'src/components/SelectMultipleLanguage';
import TooltipCustomize from 'src/components/TooltipCustomize';
import { ILanguage } from 'src/models/general';
import {
  reducerUpdateStreamOnlineMediaSelected,
  reducerUpdateFilterLanguages
} from 'src/features/onlineMediaStream';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import useOnlineMedia from 'src/services/useOnlineMedia';

const BottomHeaderClippingById = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const id = pathname.split('/')[3];
  const query = useQueryUrl();
  const portal = query.get('portal');
  const dispatch = useAppDispatch();

  const projectId = useAppSelector((state) => state.users.project.id);
  const advanceSearchOnlineMedia = useAppSelector(
    (state) => state.storeAdvanceSearchOnlineMedia
  );
  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );
  const {
    articleList,
    removedArticleList,
    showRemovedArticles,
    filterLanguages,
    languages
  } = useAppSelector((state) => state.storeOnlineMediaStream);
  const timezone = useAppSelector((state) => state.users.project.timezone);

  const fillAdvanceSearchOnlineMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchOnlineMedia);
  }, [advanceSearchOnlineMedia]);

  const finalPayload = {
    ...fillAdvanceSearchOnlineMedia,
    languageList: filterLanguages.map((l) => l.language),
    projectId,
    ...dateRangeFormatted,
    clippingId: id,
    granularity: { unit: 'day', value: 1 },
    timezone,
    page: 1,
    limit: showRemovedArticles
      ? removedArticleList.data.limit
      : articleList.data.limit
  };

  const { getArticle, getTotalArticleByMedia, getRemovedArticles } =
    useOnlineMedia();

  const handleRefreshArticles = () => {
    const payload = {
      ...finalPayload,
      mediaList:
        !portal || portal === 'all-media'
          ? advanceSearchOnlineMedia.mediaList
          : [portal]
    };
    showRemovedArticles ? getRemovedArticles(payload) : getArticle(payload);
    getTotalArticleByMedia(finalPayload);
  };

  const handleSubmitLanguage = (langs: ILanguage[]) => {
    dispatch(reducerUpdateStreamOnlineMediaSelected([]));
    dispatch(reducerUpdateFilterLanguages(langs));
  };

  const pillSelectorStyle: any = {
    borderRadius: '8px',
    bgcolor: 'white',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[400]
    },
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main
      },
      '& .MuiSelect-select': {
        color: theme.palette.primary.main
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main
      }
    },
    height: 40,
    '& .MuiSelect-select': {
      color: theme.palette.grey[800],
      fontWeight: 500
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.grey[600]
    }
  };

  const buttonStyle: any = {
    borderRadius: '32px',
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
    whiteSpace: 'nowrap',
    height: 40
  };

  const [includeExcludeOpen, setIncludeExcludeOpen] = useState(false);
  const [includeExcludeMode, setIncludeExcludeMode] = useState<
    'include' | 'exclude'
  >('include');
  const [openSelectGroup, setOpenSelectGroup] = useState(false);
  const {
    groupPortalList,
    selectedGroupPortalIdOnm,
    selectedGroupPortalIdOnmExclude
  } = useAppSelector((state) => state.groupPortal);
  const { filteredList } = useFiltersByName<IGroupPortal>(groupPortalList.data);

  const { getGroupPortal } = useGroupPortal();
  useEffect(() => {
    getGroupPortal(projectId);
  }, [projectId]);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      mb={1}
      flexWrap="wrap"
      gap={2}
      alignItems="center"
    >
      {/* Left: Include/Exclude Group selector */}
      <Stack direction="row" alignItems="center">
        <Button
          variant="contained"
          sx={{
            borderStartStartRadius: '32px',
            borderEndStartRadius: '32px',
            borderEndEndRadius: 0,
            borderStartEndRadius: 0,
            boxShadow: 'none',
            bgcolor: theme.palette.primary.main,
            minWidth: 44,
            width: 44,
            height: 40,
            p: 0,
            '&:hover': { bgcolor: theme.palette.primary.dark },
            '& .MuiButton-startIcon': { m: 0 }
          }}
          onClick={() => setIncludeExcludeOpen((prev) => !prev)}
        >
          <ArrowDropDownIcon />
        </Button>
        <FormControl sx={{ width: 110 }}>
          <Select
            open={includeExcludeOpen}
            onOpen={() => setIncludeExcludeOpen(true)}
            onClose={() => setIncludeExcludeOpen(false)}
            value={includeExcludeMode}
            IconComponent={null}
            sx={{
              borderRadius: 0,
              bgcolor: 'white',
              height: 40,
              '& .MuiOutlinedInput-notchedOutline': {
                borderLeft: 'none',
                borderColor: theme.palette.primary.main
              },
              '& .MuiSelect-select': {
                py: 0,
                fontWeight: 600,
                fontSize: 14,
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                height: '100% !important'
              },
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.main
              }
            }}
            onChange={(e) => setIncludeExcludeMode(e.target.value as any)}
          >
            <MenuItem value={'include'}>Include</MenuItem>
            <MenuItem value={'exclude'}>Exclude</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ width: 160 }}>
          <Select
            sx={{
              borderStartEndRadius: '32px',
              borderEndEndRadius: '32px',
              borderStartStartRadius: 0,
              borderEndStartRadius: 0,
              bgcolor: 'white',
              height: 40,
              '& .MuiOutlinedInput-notchedOutline': {
                borderLeft: 'none',
                borderColor: theme.palette.primary.main
              },
              '& .MuiSelect-select': {
                py: 0,
                fontWeight: 600,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.primary.main,
                height: '100% !important'
              },
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.main
              }
            }}
            open={openSelectGroup}
            onClose={() => {
              setOpenSelectGroup(false);
              if (includeExcludeMode === 'exclude') {
                dispatch(
                  reducerUpdateAdvanceSearchOnmExcludeMediaList(
                    filteredList
                      .filter((el) =>
                        selectedGroupPortalIdOnmExclude.includes(el.id)
                      )
                      .flatMap((el) => el.portals)
                  )
                );
              } else {
                dispatch(
                  reducerUpdateAdvanceSearchOnmMediaList(
                    filteredList
                      .filter((el) => selectedGroupPortalIdOnm.includes(el.id))
                      .flatMap((el) => el.portals)
                  )
                );
              }
            }}
            onOpen={() => setOpenSelectGroup(true)}
            value={'always-display'}
          >
            <MenuItem value={'always-display'} sx={{ display: 'none' }}>
              {filteredList
                .filter((el) =>
                  includeExcludeMode === 'exclude'
                    ? selectedGroupPortalIdOnmExclude.includes(el.id)
                    : selectedGroupPortalIdOnm.includes(el.id)
                )
                .map((el) => el.displayName)
                .join(',') || 'Group'}
            </MenuItem>
            <Box sx={{ p: theme.spacing(2) }}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Group
              </Typography>
              <TableSelectGroupPortal
                groupPortals={filteredList}
                selectedGroupByIds={
                  includeExcludeMode === 'exclude'
                    ? selectedGroupPortalIdOnmExclude
                    : selectedGroupPortalIdOnm
                }
                loading={groupPortalList.loading}
                onClick={(groupObj) => {
                  if (includeExcludeMode === 'exclude')
                    dispatch(
                      toggleSelectedGroupPortalIdOnmExclude(groupObj.id)
                    );
                  else dispatch(toggleSelectedGroupPortalIdOnm(groupObj.id));
                }}
              />
            </Box>
          </Select>
        </FormControl>
      </Stack>

      {/* Right: Date, Language, Refresh aligned with Group selector */}
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ width: 170 }}>
          <SelectDateRangeRetail
            selectProps={{
              size: 'small',
              sx: pillSelectorStyle,
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon
                    fontSize="small"
                    sx={{ color: 'inherit', ml: 1 }}
                  />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <SelectMultipleLanguage
          languages={languages}
          onSubmit={handleSubmitLanguage}
          buttonSx={{
            ...buttonStyle,
            borderRadius: '8px',
            borderColor: theme.palette.grey[400],
            color: theme.palette.grey[800],
            fontWeight: 500,
            bgcolor: 'white',
            border: `1px solid ${theme.palette.grey[400]}`,
            '&:hover': {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              bgcolor: 'rgba(0, 159, 227, 0.04)',
              border: `1px solid ${theme.palette.primary.main}`,
              '& .MuiButton-startIcon': {
                color: theme.palette.primary.main
              },
              '& .MuiButton-endIcon': {
                color: theme.palette.primary.main
              }
            },
            '& .MuiButton-startIcon': {
              color: theme.palette.grey[600]
            },
            '& .MuiButton-endIcon': {
              color: theme.palette.grey[600]
            }
          }}
        />
        <TooltipCustomize title="Refresh" placement="top">
          <IconButton
            onClick={handleRefreshArticles}
            sx={{
              bgcolor: 'white',
              border: `1px solid ${theme.palette.grey[400]}`,
              borderRadius: '8px',
              height: 40,
              width: 40,
              '&:hover': {
                bgcolor: 'rgba(0, 159, 227, 0.04)',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main
              }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </TooltipCustomize>
      </Stack>
    </Stack>
  );
};

export default BottomHeaderClippingById;
